import React, { useState, useEffect, useRef } from "react";
import { useHistory } from 'react-router-dom';
import { useIntl } from "react-intl";

import jquery from "jquery";
import { Remarkable } from "remarkable";
import hljs from "highlight.js";
import PropTypes from "prop-types";

import * as SearchUtils from "../../Utils/SearchUtils";
import * as SearchConstant from "../../Constants/SearchConstant";
import * as DocumentationConstant from "../../Constants/DocumentationConstant";
import * as DocumentationUtils from "../../Utils/DocumentationUtils";
import * as MarkdownUtils from "../../Utils/MarkdownUtils";
import * as GenericFetch from "../../../middleware/Request/GenericFetch";
import ErrorPanel, { ErrorSize } from "../../UI/ErrorPanel/ErrorPanel";

import codegroup from "../../../library/remarkable-codegroup";
import codecopy from  "../../../library/remarkable-codecopy"

import "./highlight.scss";
import LogService from "../../../middleware/CommonSdk/LogService";

const LOG_TAG = "MARKDOWN";
export const MARKDOWN_DOC_MAIN_ID = "MARKDOWN_DOC_MAIN_ID";
const SCROLL_OFFSET = -10;

export const ResultDisplayMode = {
    Normal: "Normal",
    SearchSummary: "SearchSummary"
};

// Actual default values
const md = new Remarkable({
    highlight: (str, lang) => {
        if (lang && hljs.getLanguage(lang)) {
            try {
                return codecopy(str) + hljs.highlight(str, { language: lang }).value;
            } catch (err) {
                return "";
            }
        }

        if (["mermaid"].indexOf(lang) !== -1) {
            return '<div class="mermaid">' + str + "</div>";
        }

        try {
            return hljs.highlightAuto(str).value;
        } catch (err) {
            return "";
        }
    },
    html: true
}).use(codegroup);

md.inline.ruler.enable([
    'footnote_inline',
    'ins',
    'mark',
    'sub',
    'sup'
  ]);

  md.core.ruler.enable([
    'abbr'
  ]);  

const Markdown2HTML = (props) => {
    const [mdHtml, setMdHtml] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const mdFile = useRef({
        path: "",
        md: "",
    });
    const searchRef = useRef({
        searchValue: "",
        searchOccurenceOffsetIndex: ""
    });

    const history = useHistory();
    const intl = useIntl();

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect didmount");

        const clickListener = event => {
            onClickHandler(event);
        };
        if (props.displayMode === ResultDisplayMode.Normal) {
            document.addEventListener("click", clickListener);
        }
        return () => {
            LogService.debug(LOG_TAG, "useEffect unmount");
            if (props.displayMode === ResultDisplayMode.Normal) {
                document.removeEventListener("click", clickListener);
            }
        };
    }, []);

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect src", mdFile.current.path, props.srcLocation.key);

        const newSearchValue = SearchUtils.getSearchValue(props.srcLocation);
        const newSearchOffset = SearchUtils.getSearchOccurenceOffsetIndex(props.srcLocation);
        if (searchRef.current.searchValue !== newSearchValue || searchRef.current.searchOccurenceOffsetIndex !== newSearchOffset) {
            searchRef.current = {
                searchValue: newSearchValue,
                searchOccurenceOffsetIndex: newSearchOffset
            };
        }

        if (isPageAlreadyLoaded()) {
            updateHtml();
            if (props.displayMode === ResultDisplayMode.Normal) {
                scrollToAnchor();
            }
        } else if (props.srcLocation.pathname) {
            loadMarkdownPage(props.srcLocation.pathname);
        }
    }, [props.srcLocation]);

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect html");
        if (mdHtml.length > 0) {
            if (isPageAlreadyLoaded() && props.displayMode === ResultDisplayMode.Normal) {
                scrollToAnchor();
            }
        }
    }, [mdHtml]);

    const updateHtml = () => {
        LogService.trace(LOG_TAG, "updateHtml");

        let htmlData = "";
        if (mdFile.current.md.length > 0) {
            htmlData = renderHTML(mdFile.current.md);
            if (searchRef.current.searchValue.length > 0) {
                htmlData = highlightSearchedValue(searchRef.current.searchValue, htmlData);
            }
        }
        setMdHtml(htmlData);
        if (props.onPageLoaded)
            props.onPageLoaded();
    }

    const loadMarkdownPage = (mdFilePath) => {
        LogService.trace(LOG_TAG, "loadMarkdownPage", "enter");

        if (DocumentationUtils.isMarkdownFile(mdFilePath)) {
            LogService.debug(LOG_TAG, "loadMarkdownPage", "fetch page:", mdFilePath);

            GenericFetch.fetchFile(mdFilePath)
                .then(data => {
                    LogService.info(LOG_TAG, "loadMarkdownPage success");
                    const mdContent = modifyRenderedDocumentation(data, mdFilePath);

                    LogService.debug(LOG_TAG, "loadMarkdownPage", "setMdFile");
                    mdFile.current = {
                        path: mdFilePath,
                        md: mdContent
                    };
                    setErrorMessage("");
                    updateHtml();
                })
                .catch(errorPayload => {
                    LogService.error(LOG_TAG, "[loadMarkdownPage] error", errorPayload?.errorMessage);
                    mdFile.current = {
                        path: mdFilePath,
                        md: ""
                    };
                    let errorMessage = intl.messages.documentLoadError;
                    if (errorPayload.errorCode === 404)
                        errorMessage = intl.messages.documentNotFoundError;
                    setErrorMessage(errorMessage);
                });
        } else {
            LogService.warn(LOG_TAG, "loadMarkdownPage", "not a markdown page");
            mdFile.current = {
                path: mdFilePath,
                md: ""
            };
            setErrorMessage(intl.messages.documentLoadError);
        }
    }

    const isPageAlreadyLoaded = () => {
        if (mdFile.current.path === props.srcLocation.pathname && mdFile.current.md.length > 0) {
            return true;
        }
        return false;
    }

    const onClickHandler = (event) => {
        if (event.target.hostname && (window.location.hostname !== event.target.hostname)) {
            LogService.info(LOG_TAG, "onClickHandler", "redirect to :" + event.target.hostname);
            return;
        }

        if (event.target.hash) {
            event.preventDefault();//to prevent reloading entire page
            let newUrl = event.target.hash;
            if (newUrl.startsWith("#/")) {//not a hash, but link to another doc
                newUrl = "/" + newUrl;
                LogService.info(LOG_TAG, "onClickHandler", " new url:" + newUrl);
                history.push(newUrl);
            } else if (newUrl.startsWith("#/tab-")) {
                LogService.info(LOG_TAG, "onClickHandler", " click on tab... and do nothing");
            } else {
                history.push(newUrl);
            }
        } else if (event.target.parentElement.hash) {// ex : <a href="#Bubble"><code>Bubble</code></a>
            event.preventDefault();//to prevent reloading entire page
            let newUrl = event.target.parentElement.hash;
            if (newUrl.startsWith("#/")) {//not a hash, but link to another doc; case navbar <a ... href="#/web">...<strong>WEB</strong></a>
                newUrl = "/" + newUrl;
            }
            LogService.info(LOG_TAG, "onClickHandler", " parent hash: new url:" + newUrl);
            history.push(newUrl);
        }
    }

    const modifyRenderedDocumentation = (doc, docPath) => {
        doc = doc.replace(
            new RegExp(/(\!\[.*\]\()(?!http)/g),
            "$1" + docPath.substring(0, docPath.lastIndexOf("/") + 1)
        );

        // Remove attribute link with . if exists
        doc = doc.replace(new RegExp(/\[\./g), "[");

        // Remove new keyword (optional)
        doc = doc.replace(new RegExp(/### new/g), "###");
        doc = doc.replace(new RegExp(/\[new/g), "[");

        doc = doc.replace(new RegExp("_static_"), "_API_");
        doc = doc.replace(new RegExp("_inner_"), "_EVENTS_");
        doc = doc.replace(new RegExp("_instance_"), "_API_");
        doc = doc.replace(new RegExp(/\\&quot;/g), "");
        doc = doc.replace(new RegExp(/&quot;/g), "");

        return doc;
    }

    const renderHTML = (data) => {
        let elt = jquery(document.createElement("div"));
        elt.append(md.render(data));
        elt.find("a[href$='index.html']").attr("target", "_blank");
        elt.find("a[href$='pdf']").attr("target", "_blank");
        elt.find("a[href$='png']").attr("target", "_blank");
        elt.find("a[href^='http']").attr("target", "_blank");
        return jquery(elt).html();
    }

    const onSearchResultClick = (searchValue, occurenceOffsetIndex) => {
        LogService.trace(LOG_TAG, "onSearchResultClick");
        const mdExtensionRegex = new RegExp(DocumentationConstant.MdFileExtension + "$", "i");
        const path = mdFile.current.path.replace(mdExtensionRegex, "");
        let url = SearchUtils.addOrUpdateQueryParameterToPathString(path, SearchConstant.SearchTagName, searchValue);
        history.push(url + "#" + SearchConstant.SearchOccurenceOffsetIndexTagName + "=" + occurenceOffsetIndex);
    }

    const regexString = (searchValue) => {
        return "\\b" + searchValue;
    }

    const findSearchedTextInHtmlPage = (searchValue) => {
        let foundValues = [];
        searchValue.split(" ").map((value) => {
            const regexLine = new RegExp(".*(" + getSearchHighlightHtmlCodeInString(value) + ").*", "gi");//regex + entire line; g all matches; i ignore case

            for (const searchValueMatchIterator of mdHtml.matchAll(regexLine)) {
                const existingIndex = foundValues.findIndex((item) => item.index === searchValueMatchIterator.index);
                if (existingIndex < 0) {
                    foundValues.push(searchValueMatchIterator);
                }
            };
        });
        return foundValues;
    }

    const getSearchHighlightHtmlCodeInString = (searchValue) => {
        return "<span class=\"bg-fog\">" + searchValue + "</span>"; //double quote for class, because jquery returns \"
    }

    const highlightSearchedValue = (searchValue, htmlText) => {
        let highlightedText = htmlText;
        searchValue.split(" ").map((value) => {
            const regex = new RegExp(regexString(value), "gi");

            const htmlTagInPage = MarkdownUtils.getTagInHtmlPage(highlightedText);
            highlightedText = highlightedText.replace(regex, (foundValue, index) => {
                if (MarkdownUtils.isInHtmlTagAttributes(index, htmlTagInPage)) {
                    return foundValue;
                } else {
                    return getSearchHighlightHtmlCodeInString(foundValue);
                }
            });
        });
        return highlightedText;
    }

    const displaySearchValues = () => {
        let displayResult = null;
        let firstIndex = 0;

        const foundValues = findSearchedTextInHtmlPage(searchRef.current.searchValue);
        if (foundValues && foundValues.length > 0) {
            displayResult = foundValues.map((item, i) => {
                let htmlText = item[0];
                const index = item.index;
                if (i === 0)
                    firstIndex = index;

                //add ul if line begins with li
                const firstLi = new RegExp("^<li>");
                const lastLi = new RegExp("</li>$");
                htmlText = htmlText.replace(firstLi, "<ul><li>");
                htmlText = htmlText.replace(lastLi, "</li></ul>");
                htmlText = htmlText.replace(/href=".*?"/, "");//disable link

                return (
                    <li key={i}
                        dangerouslySetInnerHTML={{ __html: htmlText }}
                        className="list-clickable"
                        onClick={() => onSearchResultClick(searchRef.current.searchValue, index)}
                    />
                );
            });

            let title = DocumentationUtils.getDocumentMenuPath(mdFile.current.path, true);
            if (title.length === 0) {
                title = DocumentationUtils.getPrettyTitleFromPath(mdFile.current.path);
            }
            return (
                <ul id={MARKDOWN_DOC_MAIN_ID} className="d-block mt-0 list-unstyled">
                    {title.length > 0 &&
                        <h3
                            className="text-center mt-2"
                            onClick={() => onSearchResultClick(searchRef.current.searchValue, firstIndex)}
                        >
                            {title}
                        </h3>
                    }
                    {displayResult}
                </ul>
            );
        } else
            return null;
    }

    const scrollToOffsetForSearchIndex = (searchValue, searchIndex) => {
        LogService.trace(LOG_TAG, "scrollToOffsetForSearchIndex");

        const foundValues = findSearchedTextInHtmlPage(searchValue);
        if (foundValues && foundValues.length > 0) {
            const foundIndex = foundValues.findIndex((item) => item.index === searchIndex);
            if (foundIndex >= 0) {
                let htmlText = foundValues[foundIndex][0];
                jquery(document).ready(function () {
                    let resultElements = jquery("#" + MARKDOWN_DOC_MAIN_ID).find("*").filter(function () {
                        let found = false;
                        if (this.outerHTML.indexOf(htmlText) >= 0) {
                            found = true;
                        }
                        return found;
                    });

                    //filter father, keep child
                    resultElements = jquery(resultElements).filter(function () {//ie child element is also found in parent ( table ) 
                        const fatherElement = this;
                        let foundFatherAndChild = false;
                        jquery(resultElements).each(function () {
                            const foundChild = jquery(fatherElement).find(this);
                            if (foundChild.length > 0) {
                                foundFatherAndChild = true;
                            }
                        });
                        return !foundFatherAndChild;
                    });

                    let occurenceOfElement = jquery(resultElements);
                    if (resultElements && resultElements.length > 1) {//ie <p><strong>Kind</strong>: instance property of <a href="#Invitation"><code>Invitation</code></a>  <br> (10 occurences)
                        let nbSameItemsToGoodOccurence = 0;
                        for (let j = 0; j < foundValues.length; j++) {
                            if (foundValues[j][0].indexOf(htmlText) === 0) {
                                nbSameItemsToGoodOccurence++;
                            }
                            if (foundValues[j].index >= searchIndex) {
                                break;
                            }
                        };
                        if (nbSameItemsToGoodOccurence === 0) {
                            occurenceOfElement = jquery(resultElements[0]);
                        } else {
                            occurenceOfElement = jquery(resultElements[nbSameItemsToGoodOccurence - 1]);
                        }
                    }

                    if (occurenceOfElement && occurenceOfElement.offset()) {
                        let offset = occurenceOfElement.offset().top + SCROLL_OFFSET; //compute element offset + offsetParent
                        window.scrollTo(0, offset);
                        LogService.debug(LOG_TAG, "scrollToOffsetForSearchIndex offset:", offset, "offsetTop", occurenceOfElement.offset().top);
                    }
                });
            }
        }
    }

    const scrollToAnchor = () => {
        LogService.trace(LOG_TAG, "scrollToAnchor");
        const hash = props.srcLocation.hash.split("#").pop();
        if (searchRef.current.searchValue.length > 0) {
            scrollToOffsetForSearchIndex(searchRef.current.searchValue, searchRef.current.searchOccurenceOffsetIndex);
        } else if (hash && hash.length > 0) {
            const element = jquery("[name='" + hash + "']");
            LogService.debug(LOG_TAG, "scrollToAnchor", "element length:" + element.length);
            if (element && element.length > 0) {
                let offset = element.offset().top; //compute element offset + offsetParent
                window.scrollTo(0, offset);
                LogService.debug(LOG_TAG, "scrollToAnchor", "offset:", offset, " offsetTop:", element[0].offsetTop, "offsetParentTop:", element[0].offsetParent.offsetTop);
            }
        } else {
            window.scrollTo(0, 0);
        }
    };

    LogService.info(LOG_TAG, "render", "enter");

    let display = null;
    if (mdHtml.length > 0) {
        display = <div id={MARKDOWN_DOC_MAIN_ID} className="tutorial" dangerouslySetInnerHTML={{ __html: mdHtml }} />;
        switch (props.displayMode) {
            case ResultDisplayMode.SearchSummary:
                display = displaySearchValues();
                break;
            default:
                break;
        }
    } else if (errorMessage?.length > 0) {
        let title = DocumentationUtils.getPrettyTitleFromPath(mdFile.current.path);

        display =
            <ul id={MARKDOWN_DOC_MAIN_ID} className="d-block mt-0 list-unstyled">
                <h3 className="text-center mt-2">{title}</h3>
                <ErrorPanel size={ErrorSize.Small} >{errorMessage}</ErrorPanel>
            </ul>
    }

    return display;
}

Markdown2HTML.propTypes = {
    srcLocation: PropTypes.shape({
        pathname: PropTypes.string,
        hash: PropTypes.string,
        search: PropTypes.string
    }),
    displayMode: PropTypes.oneOf(Object.keys(ResultDisplayMode)),
    onPageLoaded: PropTypes.func
};

Markdown2HTML.defaultProps = {
    srcLocation: {
        pathname: "",
        hash: "",
        search: ""
    },
    displayMode: ResultDisplayMode.Normal
};

export default Markdown2HTML; //withRouter removed to avoid render
